// src/enduser/eventgroupdetail.jsx
// グループ詳細（公開）。グループ内の犬を、犬種 → 性別 × クラス の順に表示する。
// ゼッケン番号（bib_no）は、採番機能ができるまで空。番号があるときだけ左に表示する。
import React from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import PubHeader from "./PubHeader";
import "./eventcatalog.css";

const API_URL = import.meta.env.VITE_API_URL;

const SEX_LABEL = { M: "牡", F: "牝" };

export default function EnduserEventGroupDetail() {
  const { eventId, groupNo } = useParams();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["event-group-detail", "public", eventId, groupNo],
    queryFn: async () => {
      const res = await axios.get(
        `${API_URL}/api/public/events/${eventId}/groups/${groupNo}/`
      );
      if (!res.data?.success) throw new Error("グループの取得に失敗しました");
      return res.data.data;
    },
    // 404（非公開・割当なし・存在しない）はリトライしない
    retry: (count, err) => err?.response?.status !== 404 && count < 1,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const notFound = isError && error?.response?.status === 404;
  const group = data?.group;

  // 戻り先は、そのグループのリングに絞ったグループ一覧（リングがなければ全体の一覧）
  const backTo =
    group?.ring_no != null
      ? `/events/${eventId}/groups?ring=${group.ring_no}`
      : `/events/${eventId}/groups`;

  return (
    <div className="pubCat">
      <PubHeader to={backTo} backLabel="戻る" title="グループ詳細" />

      {isLoading ? (
        <div className="pubCatMsg">読み込み中...</div>
      ) : isError ? (
        <div className="pubCatMsg">
          {notFound
            ? "グループが見つかりません"
            : `エラー: ${error?.message || "グループの取得に失敗しました"}`}
          <br />
          <Link to={`/events/${eventId}/groups`}>グループ一覧へ戻る</Link>
        </div>
      ) : (
        <>
          <div className="pubCatHero pubCatGrpHero">
            <div className="pubCatInner">
              <h1 className="pubCatGrpTitle">
                {group.group_no}G {group.name}
              </h1>
              <div className="pubCatGrpMeta">
                <span>
                  {group.ring_no != null && `リング ${group.ring_no}`}
                  {group.ring_no != null && group.judge_names.length > 0 && "　・　"}
                  {group.judge_names.join(" ／ ")}
                </span>
                <span>
                  {group.start_time && `${group.start_time}〜　`}
                  <span className="pubCatGrpMetaN">{group.entry_count}</span> 頭
                </span>
              </div>
            </div>
          </div>

          <div className="pubCatInner pubCatBody">
            {data.breeds.length === 0 ? (
              <div className="pubCatMsg">出陳犬は、まだいません</div>
            ) : (
              data.breeds.map((breed) => (
                <div
                  key={`${breed.breed_name}-${breed.variety_name}`}
                  className="pubCatBreed"
                >
                  <div className="pubCatBreedHd">
                    <span className="pubCatBreedName">{breed.breed_name}</span>
                    {breed.variety_name && (
                      <span className="pubCatBreedVar">{breed.variety_name}</span>
                    )}
                    <span className="pubCatBreedCnt">{breed.entry_count} 頭</span>
                  </div>

                  {breed.sections.map((sec) => (
                    <div key={`${sec.gender}-${sec.class_name}`}>
                      <div className="pubCatSec">
                        <h3 className="pubCatSecLabel">
                          <span className="pubCatSecSex">{SEX_LABEL[sec.gender] || ""}</span>
                          {sec.class_name}
                        </h3>
                        <span className="pubCatSecCnt">{sec.entry_count} 頭</span>
                      </div>

                      {sec.dogs.map((dog, i) => (
                        <div key={`${dog.dog_name}-${i}`} className="pubCatDog">
                          {dog.bib_no != null && (
                            <span className="pubCatBib">{dog.bib_no}</span>
                          )}
                          <span className="pubCatDogBody">
                            <span className="pubCatDogName">{dog.dog_name}</span>
                            {dog.handler_name && (
                              <span className="pubCatDogHandler">
                                ハンドラー　{dog.handler_name}
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
